import { FC } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { Filter, FilterOp } from 'common/types/summaryDefinitions';
import { ValueType } from 'common/types/summaryDefinitions/valueTypes/valueType';

const OP_SYMBOL: Record<FilterOp, string> = {
  '=': '=',
  '!=': '≠',
  '>': '>',
  '>=': '≥',
  '<': '<',
  '<=': '≤',
  contains: 'contains',
};

const buildChipLabel = (filter: Filter, valueTypeById: Map<string, ValueType>): string => {
  const [colType, [op, value]] = filter;
  const displayName = valueTypeById.get(colType)?.displayName ?? colType;
  const opSymbol = OP_SYMBOL[op] ?? op;
  return `${displayName} ${opSymbol} ${value}`;
};

export interface FilterChipsBarProps {
  filters: Filter[];
  valueTypeById: Map<string, ValueType>;
  // Omitted on read-only summaries: chips render without delete icons and "Clear all" is hidden.
  onClearAll?: () => void;
  onRemoveFilter?: (index: number) => void;
  isClearing?: boolean;
}

const FilterChipsBar: FC<FilterChipsBarProps> = ({
  filters,
  valueTypeById,
  onClearAll,
  onRemoveFilter,
  isClearing = false,
}) => {
  if (filters.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        pt: 0.75,
        pb: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        mb: 0.5,
      }}>
      <Typography
        variant="caption"
        color="primary"
        sx={{ fontWeight: 600, whiteSpace: 'nowrap', mr: 0.25 }}>
        Active filters:
      </Typography>
      {filters.map((filter, index) => (
        <Chip
          key={index}
          label={buildChipLabel(filter, valueTypeById)}
          size="small"
          color="primary"
          onDelete={onRemoveFilter ? () => onRemoveFilter(index) : undefined}
        />
      ))}
      {onClearAll && (
        <Button
          size="small"
          variant="text"
          color="primary"
          disabled={isClearing}
          onClick={onClearAll}
          sx={{ ml: 0.25, px: 0.5, py: 0, minWidth: 'auto', textTransform: 'none', fontWeight: 500, fontSize: '0.75rem' }}>
          Clear all
        </Button>
      )}
    </Box>
  );
};

export default FilterChipsBar;
